import type { TableFilterState } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { DialogListDataGrid, useDialogListState, type DialogListSectionProps } from '@/pages/dialogs';
import { useSampleActions } from '../../model/useSampleActions';
import { useSampleFiltersSnapshot } from '../../model/useSampleFiltersSnapshot';
import { useSampleSection } from '../../model/sampleSectionContext';
import { SamplePickingActions } from './SamplePickingActions';
import { SamplesPopover } from '../SamplesPopover/SamplesPopover';

/** Отбор пользователя, не участвующий в снимке — постоянная ссылка, чтобы снимок не пересобирался */
const NO_USER_FILTERS: TableFilterState = [];

/**
 * Секция списка диалогов раздела выборок: тот же список, что и в разделе диалогов, с отбором
 * по открытой выборке. Своё у раздела — блок выборок вместо блока пресетов и выбор записей.
 */
export const SamplesListSection: FC<DialogListSectionProps> = ({ className, controlsContainer, onLoaded }) => {
  const section = useSampleSection();
  const { sampleId, sample, canAdmin, isPicking, isPickedOnly, pickedIds, isCompositionDirty } = section;
  const { openSample, saveSample, isSaving } = useSampleActions();

  // Отметки строк ведёт раздел, пока включён выбор записей: тогда набранное переживает смену
  // отбора и остаётся тем составом, который сохраняется в выборку.
  const rowSelectionOverride = useMemo(
    () =>
      isPicking
        ? { rowSelection: section.rowSelection, onRowSelectionChange: section.onRowSelectionChange }
        : undefined,
    [isPicking, section.rowSelection, section.onRowSelectionChange],
  );

  const { gridProps, list } = useDialogListState({
    sectionKey: section.tableName,
    onLoaded,
    sectionFilters: section.sectionFilters,
    rowSelectionOverride,
    // Раздел служит отбору диалогов: отметки набирают состав выборки, а обрабатывают диалоги
    // в разделе диалогов — массовых действий здесь нет.
    hasMassActions: false,
    // Показ набранного показывает набранное целиком: отбор, из которого его набирали, сузил бы
    // показанное — ровно то, что в нём и разбирают.
    isSectionFiltersExclusive: isPickedOnly,
  });

  // В показе набранного список показывает не отбор, а набранное, поэтому отбирать в выборку
  // по фильтрам там нечего — снимок пуст, пока показ не снят.
  const { snapshot, hasSelection, isChangedFrom } = useSampleFiltersSnapshot({
    buildRequestParams: list.buildRequestParams,
    userFilters: isPickedOnly ? NO_USER_FILTERS : list.userFilters,
    contentSearch: isPickedOnly ? null : list.settings.contentSearch,
  });

  // Сохранять в выборку есть что, когда её род изменён: у выборки по фильтрам — заданный поверх
  // неё отбор, у выборки по набору записей — правки состава.
  const isFiltersDirty = sample?.type === 'filters' && hasSelection && isChangedFrom(sample.filters);
  const canSaveLayer = canAdmin && !isSaving && Boolean(sample) && (isFiltersDirty || isCompositionDirty);

  return (
    <DialogListDataGrid
      {...gridProps}
      className={className}
      controlsContainer={controlsContainer}
      isRowSelectionActive={isPicking}
      actionsSlot={<SamplePickingActions />}
      canSaveLayer={canSaveLayer}
      layerName={sample?.name}
      // Записанная выборка меняет состав списка, а показанные строки грид держит сам —
      // поэтому после сохранения список перезапрашивается.
      onSaveLayer={() =>
        sample && saveSample(sample, { filters: snapshot, conversationIds: pickedIds }, { onSuccess: list.reload })
      }
      canExitLayer={sampleId != null}
      onExitLayer={() => openSample(null)}
      layerBlock={<SamplesPopover snapshot={snapshot} hasSelection={hasSelection} />}
    />
  );
};
